import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { useEffect, useMemo, useRef, useState } from "react"
import { api } from "../../api/client"
import { useStore } from "../../store/useStore"
import type {
  HistoryEntry,
  LinkedRecordStub,
  PrimitiveDef,
  PrimitiveHistoryGroup,
  TransitiveScopeInfo,
} from "../../types"
import {
  getPrimitiveDisplayTitle,
  getScopeDisplayName,
} from "../config/scopeIcons"

export function RecordDetailPanel() {
  const { translate, currentLanguage } = useTranslation()
  const {
    detailRecordId,
    activeScopeId,
    closeRecordDetail,
    deleteRecordId,
    openDelete,
    closeDelete,
  } = useStore()
  const qc = useQueryClient()

  // Scope + records data
  const { data: pageData } = useQuery({
    queryKey: ["records", activeScopeId],
    queryFn: () => api.listRecords(activeScopeId),
  })

  const scope = pageData?.scope
  const record = pageData?.records.find((r) => r.id === detailRecordId)

  // History data
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ["history", detailRecordId],
    queryFn: () => api.getHistory(detailRecordId!),
    enabled: !!detailRecordId,
  })

  // Transitive (multi-hop) relation counts
  const { data: transitiveCounts } = useQuery({
    queryKey: ["transitive-counts", activeScopeId],
    queryFn: () => api.getTransitiveCounts(activeScopeId),
    enabled: !!detailRecordId,
  })

  const editablePrims =
    scope?.primitives.filter(
      (p) => p.type !== "relation" && p.type !== "date" && !p.config?.hidden,
    ) ?? []
  const relationPrims =
    scope?.primitives.filter((p) => p.type === "relation") ?? []

  // All transitive scopes (always shown, even with 0 records)
  const transitiveScopes = transitiveCounts?.transitive_scopes ?? []

  // Build per-primitive history lookup
  const historyByPrimName = new Map<string, HistoryEntry[]>()
  if (historyData) {
    for (const group of historyData) {
      historyByPrimName.set(group.primitive_name, group.entries)
    }
  }

  // Edit state
  const [values, setValues] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)

  // Init field values from record
  useEffect(() => {
    if (record) {
      const init: Record<string, string> = {}
      editablePrims.forEach((p) => {
        init[p.name] = record.values[p.name]?.value ?? ""
      })
      setValues(init)
      setDirty(false)
      setErrors({})
    }
  }, [detailRecordId, record?.id])

  // Delete state
  const [deleting, setDeleting] = useState(false)
  const isConfirmingDelete = deleteRecordId === detailRecordId

  if (!detailRecordId || !scope) return null

  const validate = () => {
    const errs: Record<string, string> = {}
    editablePrims.forEach((p) => {
      if (p.is_pk && !values[p.name]?.trim()) {
        errs[p.name] = translate("QMS/CRUD/REQUIRED_PK")
      }
      if (
        p.type === "number" &&
        values[p.name] &&
        isNaN(Number(values[p.name]))
      ) {
        errs[p.name] = translate("QMS/CRUD/MUST_BE_NUMBER")
      }
    })
    return errs
  }

  const handleSave = async () => {
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    setSaving(true)
    try {
      const payload: Record<
        string,
        { value: string; source: string; confirmed_by_user: boolean }
      > = {}
      editablePrims.forEach((p) => {
        payload[p.name] = {
          value: values[p.name] ?? "",
          source: "manual",
          confirmed_by_user: true,
        }
      })
      await api.updateRecord(detailRecordId, { values: payload })
      qc.invalidateQueries({ queryKey: ["records", activeScopeId] })
      setDirty(false)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.deleteRecord(detailRecordId)
      qc.invalidateQueries({ queryKey: ["records", activeScopeId] })
      closeRecordDetail()
    } finally {
      setDeleting(false)
    }
  }

  const handleFieldChange = (primName: string, v: string) => {
    setValues((prev) => ({ ...prev, [primName]: v }))
    setErrors((prev) => ({ ...prev, [primName]: "" }))
    setDirty(true)
  }

  // PK display value
  const pkPrim = scope.primitives.find((p) => p.is_pk)
  const pkValue = pkPrim ? record?.values[pkPrim.name]?.value : undefined

  // Build aggregate history summary: group by (timestamp rounded to second, source)
  const historySummary = buildHistorySummary(historyData ?? [])

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-end"
      onClick={closeRecordDetail}
    >
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative flex h-full w-full max-w-lg flex-col overflow-hidden border-l border-grey4 bg-grey2 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-grey4 bg-grey1 px-5 py-4">
          <div>
            <h3 className="font-semibold text-white">
              {getScopeDisplayName(scope, currentLanguage)}
            </h3>
            {pkValue && (
              <p className="font-mono mt-0.5 text-xs text-grey5">{pkValue}</p>
            )}
          </div>
          <button
            onClick={closeRecordDetail}
            className="text-xl leading-none text-grey5 hover:text-white"
          >
            ×
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Upload Section */}
          <UploadSection scopeId={activeScopeId} recordId={detailRecordId} />

          {/* Fields Section */}
          <div className="border-b border-grey4 px-5 py-4">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-grey5">
              {translate("QMS/DETAIL/FIELDS_SECTION")}
            </h4>
            <div className="flex flex-col gap-3">
              {editablePrims.map((p) => (
                <FieldRow
                  key={p.id}
                  prim={p}
                  value={values[p.name] ?? ""}
                  error={errors[p.name]}
                  onChange={(v) => handleFieldChange(p.name, v)}
                  history={historyByPrimName.get(p.name)}
                  historyLoading={historyLoading}
                />
              ))}
            </div>

            {relationPrims.length > 0 && (
              <div className="mt-4 flex flex-col gap-4">
                {relationPrims.map((p) => (
                  <RelationField
                    key={p.id}
                    prim={p}
                    recordId={detailRecordId}
                    scopeId={activeScopeId}
                  />
                ))}
              </div>
            )}

            {/* Transitive (multi-hop) relations — read-only */}
            {transitiveScopes.length > 0 && (
              <div className="mt-4 flex flex-col gap-4">
                {transitiveScopes.map((ts) => (
                  <TransitiveRelationField
                    key={ts.scope_id}
                    scopeInfo={ts}
                    recordId={detailRecordId}
                    originScopeId={activeScopeId}
                    count={
                      transitiveCounts?.counts[detailRecordId]?.[ts.scope_id] ??
                      0
                    }
                  />
                ))}
              </div>
            )}

            {/* Save button */}
            {dirty && (
              <div className="mt-3 flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="rounded-lg bg-yellow1 px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-yellow3 disabled:opacity-50"
                >
                  {saving
                    ? translate("QMS/SUPPLIER/SAVING")
                    : translate("QMS/COMMON/SAVE")}
                </button>
              </div>
            )}
          </div>

          {/* Change Log (aggregate summary) */}
          <div className="border-b border-grey4 px-5 py-4">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-grey5">
              {translate("QMS/DETAIL/CHANGE_LOG")}
            </h4>
            {historyLoading ? (
              <p className="py-4 text-center text-sm text-grey5">
                {translate("QMS/COMMON/LOADING")}
              </p>
            ) : historySummary.length === 0 ? (
              <p className="py-4 text-center text-sm text-grey5">
                {translate("QMS/DETAIL/NO_CHANGES_RECORDED")}
              </p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {historySummary.map((event, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-lg border border-grey4/50 bg-grey3 px-3 py-2 text-sm"
                  >
                    <span
                      className={clsx(
                        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
                        event.source === "manual"
                          ? "bg-yellow1/20 text-yellow1"
                          : "bg-success/20 text-success",
                      )}
                    >
                      {event.source === "manual" ? "M" : "I"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-grey6">
                        {translate("QMS/DETAIL/FIELDS_CHANGED", {
                          count: event.changedFields,
                          fieldLabel:
                            event.changedFields !== 1
                              ? translate("QMS/DETAIL/FIELD_PLURAL")
                              : translate("QMS/DETAIL/FIELD_SINGULAR"),
                        })}
                      </span>
                      <span className="mx-1.5 text-grey5">·</span>
                      <span className="font-mono text-xs text-grey5">
                        {event.source}
                      </span>
                    </div>
                    <span className="shrink-0 text-xs text-grey5">
                      {new Date(event.timestamp).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Delete Section */}
          {!scope.is_singleton && (
            <div className="px-5 py-4">
              <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-grey5">
                {translate("QMS/DETAIL/DANGER_ZONE")}
              </h4>
              {isConfirmingDelete ? (
                <div className="rounded-lg border border-red1/30 bg-red1/10 p-4">
                  <p className="mb-3 text-sm text-redLight">
                    {translate("QMS/DETAIL/DELETE_BODY")}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={closeDelete}
                      className="flex-1 rounded-lg border border-grey4 px-3 py-1.5 text-sm text-grey5 transition-colors hover:bg-grey3"
                    >
                      {translate("QMS/COMMON/CANCEL")}
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="flex-1 rounded-lg bg-red1 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-redLight disabled:opacity-50"
                    >
                      {deleting
                        ? translate("QMS/CRUD/DELETING")
                        : translate("QMS/DETAIL/CONFIRM_DELETE")}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => openDelete(detailRecordId)}
                  className="rounded-lg border border-red1/30 px-4 py-2 text-sm text-red1 transition-colors hover:bg-red1/10"
                >
                  {translate("QMS/DETAIL/DELETE_RECORD")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Aggregate history: group entries by (timestamp bucket, source) into events
// ---------------------------------------------------------------------------

interface HistoryEvent {
  timestamp: string
  source: string
  changedFields: number
}

function buildHistorySummary(groups: PrimitiveHistoryGroup[]): HistoryEvent[] {
  const all: HistoryEntry[] = []
  for (const g of groups) {
    for (const e of g.entries) all.push(e)
  }
  if (all.length === 0) return []

  const bucketKey = (e: HistoryEntry) => {
    const ts = new Date(e.timestamp)
    ts.setMilliseconds(0)
    return `${ts.toISOString()}|${e.source}`
  }

  const buckets = new Map<
    string,
    { timestamp: string; source: string; count: number }
  >()
  for (const e of all) {
    const key = bucketKey(e)
    const existing = buckets.get(key)
    if (existing) {
      existing.count++
    } else {
      buckets.set(key, { timestamp: e.timestamp, source: e.source, count: 1 })
    }
  }

  const events = [...buckets.values()]
    .map((b) => ({
      timestamp: b.timestamp,
      source: b.source,
      changedFields: b.count,
    }))
    .sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    )

  return events
}

// ---------------------------------------------------------------------------
// Field Row with toggleable per-field history
// ---------------------------------------------------------------------------

interface FieldRowProps {
  prim: PrimitiveDef
  value: string
  error?: string
  onChange: (v: string) => void
  history?: HistoryEntry[]
  historyLoading?: boolean
}

function FieldRow({
  prim,
  value,
  error,
  onChange,
  history,
  historyLoading,
}: FieldRowProps) {
  const { translate, currentLanguage } = useTranslation()
  const [historyOpen, setHistoryOpen] = useState(false)
  const displayTitle = getPrimitiveDisplayTitle(prim, currentLanguage)
  const hasHistory = history && history.length > 0

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="block text-sm font-medium text-grey6">
          {displayTitle}
          {prim.is_pk && (
            <span
              className="ml-1 text-xs text-warning"
              title={translate("QMS/DETAIL/PRIMARY_KEY")}
            >
              PK
            </span>
          )}
          {prim.config?.mapped_to && (
            <span
              className="ml-1 text-xs text-yellow1"
              title={translate("QMS/DETAIL/MAPPED_TO", {
                target: prim.config.mapped_to,
              })}
            >
              ⇄ {prim.config.mapped_to}
            </span>
          )}
        </label>
        {/* History toggle */}
        {(hasHistory || historyLoading) && (
          <button
            onClick={() => setHistoryOpen(!historyOpen)}
            className={clsx(
              "rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors",
              historyOpen
                ? "bg-grey4 text-grey6"
                : "text-grey5 hover:bg-grey3 hover:text-white",
            )}
          >
            {historyOpen
              ? translate("QMS/DETAIL/HIDE")
              : history
                ? translate("QMS/DETAIL/CHANGES_COUNT", {
                    count: history.length,
                    changeLabel:
                      history.length !== 1
                        ? translate("QMS/DETAIL/CHANGE_PLURAL")
                        : translate("QMS/DETAIL/CHANGE_SINGULAR"),
                  })
                : "…"}
          </button>
        )}
      </div>
      {prim.description && (
        <p className="mb-1 text-xs text-grey5">{prim.description}</p>
      )}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          prim.type === "number"
            ? "0.0"
            : translate("QMS/CRUD/FIELD_PLACEHOLDER", { title: displayTitle })
        }
        className={clsx(
          "w-full rounded-lg border bg-grey3 px-3 py-2 text-sm text-white placeholder:text-grey5 focus:outline-hidden focus:ring-1",
          error
            ? "border-red1 focus:ring-red1"
            : "border-grey4 focus:border-yellow1 focus:ring-yellow1",
        )}
      />
      {error && <p className="mt-1 text-xs text-red1">{error}</p>}

      {/* Per-field history accordion */}
      {historyOpen && (
        <div className="ml-1 mt-1.5 border-l-2 border-grey4 pl-3">
          {!history || history.length === 0 ? (
            <p className="py-1 text-xs text-grey5">
              {translate("QMS/DETAIL/NO_CHANGES")}
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              {history.map((entry) => {
                const isConfirmation = entry.old_value === entry.new_value
                const ts = new Date(entry.timestamp)
                return (
                  <div
                    key={entry.id}
                    className="flex items-baseline gap-2 py-0.5 text-xs"
                  >
                    <span className="shrink-0 tabular-nums text-grey5">
                      {isConfirmation
                        ? ts.toLocaleString("de-DE", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })
                        : ts.toLocaleDateString()}
                    </span>
                    {isConfirmation ? (
                      <span className="font-medium text-success">
                        {translate("QMS/DETAIL/CONFIRMED")}
                      </span>
                    ) : (
                      <>
                        <span className="font-mono shrink-0 text-grey5">
                          {entry.source}
                        </span>
                        <span className="min-w-0 truncate text-grey5">
                          {entry.old_value !== null ? (
                            <>
                              <span className="text-grey5 line-through">
                                {entry.old_value || "—"}
                              </span>
                              {" → "}
                              <span className="font-medium text-grey6">
                                {entry.new_value ?? "—"}
                              </span>
                            </>
                          ) : (
                            <span className="font-medium text-grey6">
                              {entry.new_value ?? "—"}
                            </span>
                          )}
                        </span>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Relation field — show linked records, add/remove
// ---------------------------------------------------------------------------

interface RelationFieldProps {
  prim: PrimitiveDef
  recordId: string
  scopeId: string
}

function RelationField({ prim, recordId, scopeId }: RelationFieldProps) {
  const { translate, currentLanguage } = useTranslation()
  const displayTitle = getPrimitiveDisplayTitle(prim, currentLanguage)
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [search, setSearch] = useState("")
  const searchRef = useRef<HTMLInputElement>(null)

  // Linked records for this relation
  const { data: preview, isLoading } = useQuery({
    queryKey: ["relation-preview", recordId, prim.name],
    queryFn: () => api.getRelationPreview(recordId, prim.name),
    enabled: !!recordId,
  })

  // All records in the related scope (for the "add" picker)
  const { data: relatedPage } = useQuery({
    queryKey: ["records", prim.related_scope_id],
    queryFn: () => api.listRecords(prim.related_scope_id!),
    enabled: addOpen && !!prim.related_scope_id,
  })

  const linkedIds = useMemo(
    () => new Set(preview?.records.map((r) => r.id) ?? []),
    [preview],
  )

  const candidates = useMemo(() => {
    if (!relatedPage) return []
    const term = search.toLowerCase()
    return relatedPage.records.filter((r) => {
      if (linkedIds.has(r.id)) return false
      if (!term) return true
      const pk = Object.values(r.values).find((v) => v)?.value ?? ""
      return (
        pk.toLowerCase().includes(term) || r.id.toLowerCase().includes(term)
      )
    })
  }, [relatedPage, linkedIds, search])

  const invalidateAll = () => {
    qc.invalidateQueries({
      queryKey: ["relation-preview", recordId, prim.name],
    })
    qc.invalidateQueries({ queryKey: ["records", scopeId] })
  }

  const addMut = useMutation({
    mutationFn: (targetId: string) =>
      api.addRelation(recordId, prim.name, targetId),
    onSuccess: invalidateAll,
  })

  const removeMut = useMutation({
    mutationFn: (targetId: string) =>
      api.removeRelation(recordId, prim.name, targetId),
    onSuccess: invalidateAll,
  })

  // Focus search input when add picker opens
  useEffect(() => {
    if (addOpen) searchRef.current?.focus()
  }, [addOpen])

  const linkedRecords = preview?.records ?? []

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <label className="text-sm font-medium text-grey6">
          {displayTitle}
          <span className="ml-1.5 text-xs font-normal text-grey5">
            {isLoading
              ? "…"
              : translate("QMS/DETAIL/LINKED_COUNT", {
                  count: linkedRecords.length,
                })}
          </span>
        </label>
        <button
          onClick={() => {
            setAddOpen(!addOpen)
            setSearch("")
          }}
          className={clsx(
            "rounded px-2 py-0.5 text-xs font-medium transition-colors",
            addOpen ? "bg-grey4 text-grey6" : "text-yellow1 hover:bg-grey3",
          )}
        >
          {addOpen
            ? translate("QMS/DETAIL/CLOSE")
            : translate("QMS/DETAIL/ADD")}
        </button>
      </div>

      {/* Linked records list */}
      {linkedRecords.length > 0 && (
        <ul className="mb-2 flex flex-col gap-1">
          {linkedRecords.map((rec) => (
            <LinkedRecordRow
              key={rec.id}
              rec={rec}
              removing={removeMut.isPending}
              onRemove={() => removeMut.mutate(rec.id)}
            />
          ))}
        </ul>
      )}

      {!isLoading && linkedRecords.length === 0 && !addOpen && (
        <p className="mb-2 text-xs italic text-grey5">
          {translate("QMS/DETAIL/NO_LINKED_RECORDS")}
        </p>
      )}

      {/* Add picker */}
      {addOpen && (
        <div className="overflow-hidden rounded-lg border border-grey4 bg-grey3">
          <div className="border-b border-grey4 px-2 py-1.5">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={translate("QMS/DETAIL/SEARCH_RECORDS", {
                scope:
                  prim.related_scope_id ??
                  translate("QMS/DETAIL/RECORDS_FALLBACK"),
              })}
              className="w-full bg-transparent text-sm text-white placeholder:text-grey5 focus:outline-hidden"
            />
          </div>
          <ul className="max-h-40 overflow-y-auto">
            {candidates.length === 0 ? (
              <li className="px-3 py-2 text-center text-xs text-grey5">
                {relatedPage
                  ? translate("QMS/COMMON/NO_RESULTS")
                  : translate("QMS/COMMON/LOADING")}
              </li>
            ) : (
              candidates.slice(0, 50).map((r) => {
                const pkPrim = relatedPage!.scope.primitives.find(
                  (p) => p.is_pk,
                )
                const pkVal = pkPrim ? r.values[pkPrim.name]?.value : null
                const label = pkVal ?? r.id
                return (
                  <li key={r.id}>
                    <button
                      onClick={() => {
                        addMut.mutate(r.id)
                        setSearch("")
                      }}
                      disabled={addMut.isPending}
                      className="w-full px-3 py-1.5 text-left text-sm text-grey6 transition-colors hover:bg-grey4 hover:text-white disabled:opacity-50"
                    >
                      {label}
                    </button>
                  </li>
                )
              })
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

function LinkedRecordRow({
  rec,
  removing,
  onRemove,
}: {
  rec: LinkedRecordStub
  removing: boolean
  onRemove: () => void
}) {
  const { translate } = useTranslation()
  return (
    <li className="group flex items-center gap-2 rounded-lg border border-grey4/50 bg-grey3 px-2.5 py-1.5">
      <div className="min-w-0 flex-1">
        <span className="block truncate text-sm text-grey6">
          {rec.name_value ?? rec.pk_value ?? translate("QMS/DETAIL/UNNAMED")}
        </span>
        {rec.pk_value && rec.name_value && (
          <span className="font-mono block truncate text-[10px] text-grey5">
            {rec.pk_value}
          </span>
        )}
      </div>
      <button
        onClick={onRemove}
        disabled={removing}
        className="shrink-0 text-xs text-grey5 opacity-0 transition-all hover:text-red1 disabled:opacity-50 group-hover:opacity-100"
        title={translate("QMS/DETAIL/REMOVE_LINK")}
      >
        ×
      </button>
    </li>
  )
}

// ---------------------------------------------------------------------------
// Transitive relation field — read-only list of multi-hop related records
// ---------------------------------------------------------------------------

const TRANSITIVE_SCOPE_COLORS: Record<string, string> = {
  supplier: "border-purple1/30",
  component: "border-yellow1/30",
  product: "border-success/30",
  customer: "border-warning/30",
  substance: "border-red1/30",
}

function TransitiveRelationField({
  scopeInfo,
  recordId,
  originScopeId,
  count,
}: {
  scopeInfo: TransitiveScopeInfo
  recordId: string
  originScopeId: string
  count: number
}) {
  const { translate } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  // Find the PK value of the current record to use for filtered query
  const { data: pageData } = useQuery({
    queryKey: ["records", originScopeId],
    queryFn: () => api.listRecords(originScopeId),
  })
  const record = pageData?.records.find((r) => r.id === recordId)
  const pkPrim = pageData?.scope.primitives.find((p) => p.is_pk)
  const pkValue = pkPrim ? record?.values[pkPrim.name]?.value : null

  // Fetch actual related records via graph traversal when expanded
  const { data: relatedPage, isLoading } = useQuery({
    queryKey: ["filtered-records", scopeInfo.scope_id, originScopeId, pkValue],
    queryFn: () =>
      api.listFilteredRecords(scopeInfo.scope_id, originScopeId, [pkValue!]),
    enabled: expanded && !!pkValue,
  })

  const relatedRecords = relatedPage?.records ?? []
  const relatedPkPrim = relatedPage?.scope.primitives.find((p) => p.is_pk)
  const relatedNamePrim = relatedPage?.scope.primitives.find(
    (p) => !p.is_pk && p.type === "string" && p.is_visible,
  )

  const viaLabel =
    scopeInfo.via_scope_names.length > 0
      ? `via ${scopeInfo.via_scope_names.join(" → ")}`
      : ""
  const borderColor =
    TRANSITIVE_SCOPE_COLORS[scopeInfo.scope_id] ?? "border-grey4/50"

  return (
    <div className={clsx("border-l-2 pl-3", borderColor)}>
      <div className="mb-1.5 flex items-center justify-between">
        <label className="text-sm font-medium text-grey6">
          {scopeInfo.scope_name}
          <span className="ml-1.5 text-xs font-normal text-grey5">
            {translate("QMS/DETAIL/LINKED_COUNT", { count })}
          </span>
          {viaLabel && (
            <span className="ml-1.5 text-[10px] font-normal text-grey5">
              {viaLabel}
            </span>
          )}
        </label>
        {count > 0 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className={clsx(
              "rounded px-2 py-0.5 text-xs font-medium transition-colors",
              expanded ? "bg-grey4 text-grey6" : "text-grey5 hover:bg-grey3",
            )}
          >
            {expanded
              ? translate("QMS/DETAIL/COLLAPSE")
              : translate("QMS/DETAIL/EXPAND")}
          </button>
        )}
      </div>

      {!expanded && (
        <p className="text-xs italic text-grey5">
          {translate("QMS/DETAIL/READ_ONLY_HINT", {
            scope: scopeInfo.via_scope_names[0] ?? scopeInfo.scope_name,
          })}
        </p>
      )}

      {expanded && isLoading && (
        <p className="py-2 text-xs text-grey5">
          {translate("QMS/COMMON/LOADING")}
        </p>
      )}

      {expanded && !isLoading && relatedRecords.length === 0 && (
        <p className="py-1 text-xs italic text-grey5">
          {translate("QMS/DETAIL/NO_LINKED_RECORDS")}
        </p>
      )}

      {expanded && relatedRecords.length > 0 && (
        <ul className="mb-1 flex flex-col gap-1">
          {relatedRecords.map((rec) => {
            const pkVal = relatedPkPrim
              ? rec.values[relatedPkPrim.name]?.value
              : null
            const nameVal = relatedNamePrim
              ? rec.values[relatedNamePrim.name]?.value
              : null
            return (
              <li
                key={rec.id}
                className="flex items-center gap-2 rounded-lg border border-grey4/50 bg-grey3 px-2.5 py-1.5"
              >
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-grey6">
                    {nameVal ?? pkVal ?? translate("QMS/DETAIL/UNNAMED")}
                  </span>
                  {pkVal && nameVal && (
                    <span className="font-mono block truncate text-[10px] text-grey5">
                      {pkVal}
                    </span>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Upload section — drop zone + analyze button
// ---------------------------------------------------------------------------

function UploadSection({
  scopeId,
  recordId,
}: {
  scopeId: string
  recordId: string
}) {
  const { translate } = useTranslation()
  const [file, setFile] = useState<File | null>(null)
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { openIngestion } = useStore()

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const f = e.dataTransfer.files[0]
    if (f) setFile(f)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) setFile(f)
  }

  return (
    <div className="border-b border-grey4 bg-grey3/50 px-5 py-4">
      <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-grey5">
        {translate("QMS/DETAIL/UPLOAD_SECTION")}
      </h4>

      {/* Drop zone */}
      <div
        className={clsx(
          "relative cursor-pointer rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors",
          dragging
            ? "border-yellow1 bg-yellow5/20"
            : file
              ? "border-success/40 bg-success/10"
              : "border-grey4 bg-grey3 hover:border-grey5",
        )}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls,.pdf,.txt"
          onChange={handleFileSelect}
          className="hidden"
        />
        {file ? (
          <>
            <p className="text-sm font-medium text-grey6">{file.name}</p>
            <p className="mt-0.5 text-xs text-grey5">
              {(file.size / 1024).toFixed(1)} KB ·{" "}
              {translate("QMS/DETAIL/CLICK_OR_DROP_REPLACE")}
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-grey5">
              {translate("QMS/CONFIG/DROP_DOCUMENT")}
            </p>
            <p className="mt-1 text-xs text-grey5">
              {translate("QMS/DETAIL/UPLOAD_FORMATS")}
            </p>
          </>
        )}
      </div>

      {/* Analyze button */}
      <button
        disabled={!file}
        onClick={() => {
          if (file) {
            openIngestion(scopeId, file, recordId)
          }
        }}
        className={clsx(
          "mt-3 w-full rounded-xl py-3 text-sm font-semibold transition-colors",
          file
            ? "bg-yellow1 text-black hover:bg-yellow3"
            : "cursor-not-allowed bg-yellow5 text-grey5",
        )}
      >
        {translate("QMS/DETAIL/ANALYZE_STRUCTURE")}
      </button>
    </div>
  )
}
